import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ArrowRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  type MonthYear,
  DISPLAY_MONTH_NAMES,
  getCurrentMonthYear,
  formatMonthYearDisplay,
  calculateDuePeriod,
  addMonthsToMonthYear,
  compareMonthYear,
  isSameMonthYear,
  isMonthYearInRange,
} from '@/lib/payments';
import { cn } from '@/lib/utils';

interface MonthYearPickerProps {
  id?: string;
  startMonth: MonthYear;
  endMonth: MonthYear;
  onChange: (start: MonthYear, end: MonthYear) => void;
  disabled?: boolean;
  className?: string;
}

const PRESET_MONTHS = [1, 2, 3, 5, 6, 10, 12] as const;

export function MonthYearPicker({
  id,
  startMonth,
  endMonth,
  onChange,
  disabled,
  className,
}: MonthYearPickerProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'from' | 'to'>('from');
  const [viewYear, setViewYear] = useState<number>(startMonth.year);

  const currentMonthYear = getCurrentMonthYear();
  const duePeriod = calculateDuePeriod(startMonth, endMonth);

  useEffect(() => {
    if (open) {
      setViewYear(activeTab === 'from' ? startMonth.year : endMonth.year);
    }
  }, [open, activeTab, startMonth.year, endMonth.year]);

  const handlePreset = (months: number) => {
    const newEnd = addMonthsToMonthYear(startMonth, months - 1);
    onChange(startMonth, newEnd);
  };

  const handleMonthClick = (monthIndex: number) => {
    const selected: MonthYear = { month: monthIndex, year: viewYear };

    if (activeTab === 'from') {
      if (compareMonthYear(selected, endMonth) > 0) {
        // If start is after end, move end with it
        onChange(selected, selected);
      } else {
        onChange(selected, endMonth);
      }
      // Auto advance to "to" tab
      setActiveTab('to');
      if (selected.year !== endMonth.year) {
        setViewYear(endMonth.year);
      }
    } else {
      if (compareMonthYear(selected, startMonth) < 0) {
        // If end selected is before start, set both to selected
        onChange(selected, selected);
      } else {
        onChange(startMonth, selected);
      }
    }
  };

  const isSingleMonth = isSameMonthYear(startMonth, endMonth);
  const displayLabel = isSingleMonth
    ? formatMonthYearDisplay(startMonth)
    : `${formatMonthYearDisplay(startMonth)} – ${formatMonthYearDisplay(endMonth)}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-10 w-full cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white px-3 text-left transition-colors',
            'hover:border-gray-300 focus-visible:border-[#4369a0] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4369a0]/25',
            open && 'border-[#4369a0] ring-1 ring-[#4369a0]/25',
            disabled && 'cursor-not-allowed bg-gray-50 text-gray-400',
            className
          )}
          aria-label="Select Due Period"
        >
          <div className="flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 text-[#4369a0]" />
            <span className="truncate text-sm font-medium text-gray-900">
              {displayLabel}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-[#4369a0]/10 px-2.5 py-0.5 text-xs font-semibold text-[#4369a0]">
              {duePeriod} {duePeriod === 1 ? 'month' : 'months'}
            </span>
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-[100] w-[340px] max-w-[95vw] rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
      >
        {/* Header Summary */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Due Period
            </p>
            <p className="text-sm font-bold text-gray-900">
              {duePeriod} {duePeriod === 1 ? 'Month' : 'Months'} (₹{duePeriod * 100})
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const cur = getCurrentMonthYear();
              onChange(cur, cur);
              setViewYear(cur.year);
              setActiveTab('from');
            }}
            className="flex items-center gap-1 text-xs font-medium text-[#4369a0] hover:underline"
            title="Reset to Current Month"
          >
            <RotateCcw className="h-3 w-3" />
            <span>Current Month</span>
          </button>
        </div>

        {/* From / To Selection Tabs */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              setActiveTab('from');
              setViewYear(startMonth.year);
            }}
            className={cn(
              'flex flex-col items-start rounded-lg border p-2 text-left transition-all',
              activeTab === 'from'
                ? 'border-[#4369a0] bg-[#4369a0]/5 ring-1 ring-[#4369a0]/30'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            )}
          >
            <span className="text-[10px] font-semibold uppercase text-gray-500">
              From Month
            </span>
            <span className="text-xs font-bold text-gray-900">
              {formatMonthYearDisplay(startMonth)}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('to');
              setViewYear(endMonth.year);
            }}
            className={cn(
              'flex flex-col items-start rounded-lg border p-2 text-left transition-all',
              activeTab === 'to'
                ? 'border-[#4369a0] bg-[#4369a0]/5 ring-1 ring-[#4369a0]/30'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            )}
          >
            <span className="text-[10px] font-semibold uppercase text-gray-500">
              To Month
            </span>
            <span className="text-xs font-bold text-gray-900">
              {formatMonthYearDisplay(endMonth)}
            </span>
          </button>
        </div>

        {/* Quick Month Presets */}
        <div className="mt-3">
          <p className="text-[11px] font-medium text-gray-500">
            Select duration from {DISPLAY_MONTH_NAMES[startMonth.month]}:
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {PRESET_MONTHS.map((months) => (
              <button
                key={months}
                type="button"
                onClick={() => handlePreset(months)}
                className={cn(
                  'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                  duePeriod === months
                    ? 'border-[#4369a0] bg-[#4369a0] text-white shadow-xs'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                )}
              >
                {months} {months === 1 ? 'mo' : 'mos'}
              </button>
            ))}
          </div>
        </div>

        {/* Year Navigation */}
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2.5">
          <button
            type="button"
            onClick={() => setViewYear((y) => y - 1)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-gray-900">{viewYear}</span>
            <span className="text-[11px] font-medium text-[#4369a0]">
              ({activeTab === 'from' ? 'Picking From' : 'Picking To'})
            </span>
          </div>
          <button
            type="button"
            onClick={() => setViewYear((y) => y + 1)}
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Month Grid */}
        <div className="mt-2.5 grid grid-cols-4 gap-1.5">
          {DISPLAY_MONTH_NAMES.map((monthName, index) => {
            const item: MonthYear = { month: index, year: viewYear };
            const isStart = isSameMonthYear(item, startMonth);
            const isEnd = isSameMonthYear(item, endMonth);
            const inRange = isMonthYearInRange(item, startMonth, endMonth);
            const isCurrent = isSameMonthYear(item, currentMonthYear);

            return (
              <button
                key={monthName}
                type="button"
                onClick={() => handleMonthClick(index)}
                className={cn(
                  'relative flex h-9 items-center justify-center rounded-md text-xs font-medium transition-all',
                  (isStart || isEnd) &&
                    'bg-[#4369a0] text-white font-semibold shadow-xs hover:bg-[#3a5d8f]',
                  inRange &&
                    !isStart &&
                    !isEnd &&
                    'bg-[#4369a0]/15 text-[#4369a0] font-semibold hover:bg-[#4369a0]/25',
                  !inRange &&
                    !isStart &&
                    !isEnd &&
                    'text-gray-700 hover:bg-gray-100',
                  isCurrent &&
                    !isStart &&
                    !isEnd &&
                    'ring-1 ring-inset ring-[#4369a0]/40 font-semibold'
                )}
              >
                {monthName}
                {isCurrent && !isStart && !isEnd && (
                  <span className="absolute bottom-1 h-1 w-1 rounded-full bg-[#4369a0]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Done Button */}
        <div className="mt-3.5 flex items-center justify-end border-t border-gray-100 pt-2.5">
          <Button
            type="button"
            size="sm"
            onClick={() => setOpen(false)}
            className="h-8 bg-[#4369a0] px-4 text-xs font-medium text-white hover:bg-[#3a5d8f]"
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
